import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasCartLineContext, withCartLine } from "@insite/client-framework/Components/CartLineContext";
import { Cart } from "@insite/client-framework/Services/CartService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import { PromotionModel } from "@insite/client-framework/Types/ApiModels";
import ProductBrand, { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import ProductDescription, { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import ProductImage, { ProductImageStyles } from "@insite/content-library/Components/ProductImage";
import ProductPartNumbers, { ProductPartNumbersStyles } from "@insite/content-library/Components/ProductPartNumbers";
import ProductPrice, { ProductPriceStyles } from "@insite/content-library/Components/ProductPrice";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import CartLineQuantity, { CartLineQuantityStyles } from "@insite/content-library/Widgets/Cart/CartLineQuantity";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import LazyImage from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { comingSoonDiscontinueDates } from "@tKgpco/Common/commonFunctions";
import CartLineNotes, { CartLineNotesStyles } from "@tKgpco/Overrides/Widgets/Cart/CartLineNotes";
import { COLORS, TYPOGRAPHY } from "@tKgpco/theme/design";
import React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    cart: Cart;
    promotions?: PromotionModel[];
    showSavingsAmount: boolean;
    showSavingsPercent: boolean;
    editable: boolean;
    showInvalidPriceMessage?: boolean;
    extendedStyles?: CartLineCardExpandedStyles;
}

const mapStateToProps = (state: ApplicationState) => ({
    enableVat: getSettingsCollection(state).productSettings.enableVat,
    vatPriceDisplay: getSettingsCollection(state).productSettings.vatPriceDisplay,
    showPricing: state.context.showPricing,
    isCheckoutReviewAndSubmitPage: getCurrentPage(state).type === "CheckoutReviewAndSubmitPage",
    isAuthenticated: state.context.session?.isAuthenticated && !state.context.session?.isGuest,
    properties: state.context.settings.properties,
});

type Props = OwnProps & HasCartLineContext & ReturnType<typeof mapStateToProps>;

export interface CartLineCardExpandedStyles {
    cartLineNotes?: CartLineNotesStyles;
    price?: ProductPriceStyles;
    quantity?: CartLineQuantityStyles;
    container?: GridContainerProps;
    productImageGridItem?: GridItemProps;
    productImage?: ProductImageStyles;
    orderLineInfoGridItem?: GridItemProps;
    orderLineInfoContainer?: GridContainerProps;
    productDescriptionAndPartNumbersGridItem?: GridItemProps;
    dateInfoGridItem?: GridItemProps;
    productMessageGridItem?: GridItemProps;
    productDescriptionAndPartNumbersContainer?: GridContainerProps;
    productBrand?: ProductBrandStyles;
    productBrandAndDescriptionGridItem?: GridItemProps;
    productDescription?: ProductDescriptionStyles;
    configurationGridItem?: GridItemProps;
    configurationOptionText?: TypographyProps;
    productPartNumbersGridItem?: GridItemProps;
    productPartNumbers?: ProductPartNumbersStyles;
    orderLineNotesGridItem?: GridItemProps;
    notesHeadingAndText?: SmallHeadingAndTextStyles;
    costCodeGridItem?: GridItemProps;
    costCodeValueText?: TypographyPresentationProps;
    costCodeLabelText?: TypographyPresentationProps;
    productPriceAndQuantityGridItem?: GridItemProps;
    productPriceAndQuantityContainer?: GridContainerProps;
    priceGridItem?: GridItemProps;
    priceHeadingAndText?: SmallHeadingAndTextStyles;
    promotionNameText?: TypographyProps;
    quantityGridItem?: GridItemProps;
    qtyHeadingAndText?: SmallHeadingAndTextStyles;
    extendedUnitNetPriceGridItem?: GridItemProps;
    extendedUnitNetPrice?: SmallHeadingAndTextStyles;
    subtotalWithoutVatText?: SmallHeadingAndTextStyles;
    vatAmount?: SmallHeadingAndTextStyles;
    cartLineErrorMessageGridItem?: GridItemProps;
    cartLineErrorMessageText?: TypographyProps;
    fromDate?: InjectableCss;
    earlistShipDateLabel?: InjectableCss;
    earlistShipDate?: InjectableCss;
    esdWrapper?: InjectableCss;
    cartMsg?: InjectableCss;
    seeLeadTimeLink?: LinkPresentationProps;
    seeLeadTimeLinkGridItem?: GridItemProps;
    tooltipText?: InjectableCss;
    badgeImgWrapper?: InjectableCss;
    productImageWrapper?: InjectableCss;
    itemNotFound?: InjectableCss;
    notFoundLabel?: TypographyPresentationProps;
}

export const cartLineCardExpandedStyles: CartLineCardExpandedStyles = {
    container: {
        gap: 20,
        css: css`
            border: 1px solid #e1e7ed;
            border-radius: 10px;
            padding: 18px 18px;
            & :not(:last-child) {
                margin-bottom: 5px;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            padding: 10px 10px;
                        `,
                    ],
                    "max",
                )}
        `,
    },
    productImageGridItem: {
        width: 2,
        printWidth: 1,
        css: css`
            @media print {
                max-height: 83px;
                max-width: 83px;
                padding: 0;
            }
        `,
    },
    orderLineInfoGridItem: {
        width: 10,
        printWidth: 11,
    },
    orderLineInfoContainer: {
        gap: 15,
    },
    productDescriptionAndPartNumbersGridItem: {
        width: [12, 12, 6, 6, 6],
        printWidth: 5,
        css: css`
            padding-bottom: 0;
        `,
    },

    dateInfoGridItem: {
        width: [12, 12, 6, 6, 6],
        printWidth: 6,
        css: css`
            padding-bottom: 0;
        `,
    },

    productMessageGridItem: {
        width: 12, // [12, 12, 6, 6, 6],
        css: css`
            display: block;
            padding-bottom: 0;
        `,
    },

    productDescriptionAndPartNumbersContainer: {
        gap: 15,
    },
    productBrand: {
        nameText: {
            color: "text.main",
        },
    },
    productBrandAndDescriptionGridItem: {
        css: css`
            flex-direction: column;
            font-size: ${TYPOGRAPHY.size.smaller};
            padding-bottom: 0;
        `,
        width: 12,
    },
    configurationGridItem: {
        width: 12,
        css: css`
            flex-direction: column;
        `,
    },
    productPartNumbersGridItem: {
        width: 12,
        css: css`
            padding-bottom: 0;
        `,
    },
    orderLineNotesGridItem: {
        width: 12,
        css: css`
            padding-bottom: 0;
            p:first-child {
                font-size: ${TYPOGRAPHY.size.smallest};
                font-weight: 500;
                margin-bottom: 0;
            }
            p {
                font-size: ${TYPOGRAPHY.size.smallest};
            }
        `,
    },
    costCodeGridItem: { width: 12 },
    costCodeLabelText: {
        weight: "bold",
        css: css`
            margin-right: 10px;
        `,
    },
    productPriceAndQuantityGridItem: {
        width: [12, 12, 6, 6, 6],
        printWidth: 7,
    },
    productPriceAndQuantityContainer: {
        gap: 15,
        css: css`
            font-size: ${TYPOGRAPHY.size.tiny};
        `,
    },
    priceGridItem: {
        width: [12, 5, 12, 5, 5],
        printWidth: 5,
        css: css`
            flex-direction: column;
            p {
                font-size: ${TYPOGRAPHY.size.smaller} !important;
            }
        `,
    },
    price: {
        price: {
            priceText: {
                // weight: "normal",
                // variant: "p",
                transform: "uppercase",
                weight: 600,
                color: COLORS.link.base,
            },
        },
        priceLabelText: {
            variant: "p",
            transform: "uppercase",
            weight: 600,
            css: css`
                margin-bottom: 0.25rem !important;
            `,
        },
    },
    quantityGridItem: {
        width: 3,
        css: css`
            p[data-test-selector^="heading"] {
                font-weight: 600;
                font-size: ${TYPOGRAPHY.size.smaller};
            }
            p[data-test-selector^="text"] {
                font-weight: 600;
                font-size: ${TYPOGRAPHY.size.tiny};
            }
        `,
    },
    extendedUnitNetPriceGridItem: {
        width: 4,
        css: css`
            p[data-test-selector^="heading"] {
                font-weight: 600;
                font-size: ${TYPOGRAPHY.size.smaller};
            }
            p[data-test-selector^="text"] {
                font-weight: 600;
                font-size: ${TYPOGRAPHY.size.tiny};
            }
        `,
    },
    extendedUnitNetPrice: {
        text: {
            weight: 700,
        },
    },

    productPartNumbers: {
        erpNumberGridItem: {
            width: [12, 4, 8, 8, 8],
            css: css`
                flex-direction: column;
                flex-direction: row;
                align-items: baseline;
            `,
        },
        // erpNumberLabelText: { variant: "legend" },
        erpNumberLabelText: {
            variant: "h5",
            color: COLORS.lightBlack,
            css: css`
                margin-right: 5px;
            `,
        },
        erpNumberValueText: {
            size: TYPOGRAPHY.size.tiny,
            weight: TYPOGRAPHY.weight.base,
            color: COLORS.lightBlack,
        },
        customerNameGridItem: {
            width: [12, 4, 6, 6, 6],
            css: css`
                flex-direction: column;
                flex-direction: row;
                align-items: baseline;
            `,
        },
        // customerNameLabelText: { variant: "legend" },
        customerNameLabelText: { variant: "h5", color: COLORS.lightBlack },
        customerNameValueText: {
            size: TYPOGRAPHY.size.tiny,
            weight: TYPOGRAPHY.weight.base,
            color: COLORS.lightBlack,
            css: css`
                white-space: nowrap;
            `,
        },
        manufacturerItemGridItem: {
            width: [12, 4, 6, 6, 6],
            css: css`
                flex-direction: row;
                align-items: baseline;
            `,
        },
        manufacturerItemLabelText: { variant: "h5", color: COLORS.lightBlack },
        manufacturerItemValueText: {
            size: TYPOGRAPHY.size.tiny,
            weight: TYPOGRAPHY.weight.base,
            color: COLORS.lightBlack,
        },
    },
    esdWrapper: {
        css: css`
            width: 50%;
        `,
    },
    fromDate: {
        css: css`
            width: 50%;
        `,
    },
    earlistShipDateLabel: {
        css: css`
            font-size: ${TYPOGRAPHY.size.smallest};
            font-weight: ${TYPOGRAPHY.weight.bold};
            color: #1a1a1a;
            margin: 0;
        `,
    },
    earlistShipDate: {
        css: css`
            font-size: ${TYPOGRAPHY.size.smallest};
            margin: 10px 0;
            color: #1a1a1a;
            margin: 0;
        `,
    },
    cartMsg: {
        css: css`
            width: 100%;
            font-size: ${TYPOGRAPHY.size.tiny};
            color: #fc3939;
        `,
    },
    cartLineErrorMessageGridItem: {
        width: 12,
    },
    cartLineErrorMessageText: {
        color: "danger",
    },
    seeLeadTimeLink: {
        css: css`
            & :hover .tooltipText {
                visibility: visible;
            }
        `,
    },
    seeLeadTimeLinkGridItem: {
        width: 5,
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        justify-content: flex-end;
                    `,
                    null,
                    null,
                    null,
                    null,
                ])}
            > span {
                background: #e1e7ed;
                padding: 3px 4px;
                border-radius: 4px;
                margin: 0 auto;
                > span {
                    color: #353432 !important;
                    font-size: ${TYPOGRAPHY.size.vTiny};
                    @media (max-width: 1199px) and (min-width: 992px) {
                        font-size: 10px !important;
                    }
                }
            }
        `,
    },
    tooltipText: {
        css: css`
            visibility: hidden;
            width: auto;
            background-color: black;
            color: #fff;
            text-align: center;
            border-radius: 6px;
            padding: 5px 6px;
            opacity: 0.8;
            position: absolute;
            z-index: 1;
            margin-top: 10px;
        `,
    },
    badgeImgWrapper: {
        css: css`
            position: absolute;
            left: 50%;
            top: 50%;
            width: 65%;
            height: 65%;
            transform: translate(-50%, -50%);
            pointer-events: none;
        `,
    },
    productImageWrapper: {
        css: css`
            position: relative;
        `,
    },
    itemNotFound: {
        css: css`
            font-size: 14px;
            margin: 12px 0px;
            span {
                padding-right: 5px;
            }
        `,
    },
    notFoundLabel: {
        size: 14,
        weight: 700,
    },
};

const CartLineCardExpanded: React.FC<Props> = ({
    cart,
    cartLine,
    showSavingsAmount,
    showSavingsPercent,
    promotions,
    editable,
    enableVat,
    vatPriceDisplay,
    showInvalidPriceMessage,
    showPricing,
    isCheckoutReviewAndSubmitPage,
    extendedStyles,
    isAuthenticated,
    properties,
}: Props) => {
    const [styles] = React.useState(() => mergeToNew(cartLineCardExpandedStyles, extendedStyles));
    let showLeadTime = cartLine.properties?.isDropShip
        ? cartLine.properties?.isDropShip?.toLowerCase() === "true"
        : false;
    if (
        cartLine.properties?.isDropShip &&
        cartLine.properties?.isDropShip?.toLowerCase() === "false" &&
        cartLine.qtyOnHand < cartLine.qtyOrdered! &&
        cartLine.properties?.showContactUsMessage !== "true"
    ) {
        showLeadTime = true;
    }
    let leadtime = "";
    if (window.localStorage.getItem("customInventory")) {
        const inventory = JSON.parse(window.localStorage.getItem("customInventory") || "null");
        if (inventory && inventory.length > 0) {
            const filterInventory = inventory.filter((data: any) => data.productId === cartLine.productId);
            if (filterInventory.length > 0) {
                leadtime = filterInventory[0].additionalResults?.leadTime ?? "";
            }
        }
    }
    const { comingSoonImagePath, discontinuedImagePath } = properties;
    const { isComingSoon, isDiscontinued } = comingSoonDiscontinueDates(cartLine);
    let showPriceMessage = true;
    if (cartLine.properties?.showContactUsMessage?.toLowerCase() === "true") {
        showPriceMessage = false;
    }
    return (
        <GridContainer
            {...styles.container}
            data-test-selector={`cartline_expanded_${cartLine.productId}_${cartLine.unitOfMeasure}`}
        >
            <GridItem {...styles.productImageGridItem}>
                <StyledWrapper {...styles.productImageWrapper}>
                    <ProductImage product={cartLine} extendedStyles={styles.productImage} />
                    {isAuthenticated && isComingSoon && (
                        <StyledWrapper className="coming-soon" {...styles.badgeImgWrapper}>
                            <LazyImage src={comingSoonImagePath} />
                        </StyledWrapper>
                    )}
                    {isAuthenticated && isDiscontinued && (
                        <StyledWrapper className="discontinued" {...styles.badgeImgWrapper}>
                            <LazyImage src={discontinuedImagePath} />
                        </StyledWrapper>
                    )}
                </StyledWrapper>
            </GridItem>
            <GridItem {...styles.orderLineInfoGridItem}>
                <GridContainer {...styles.orderLineInfoContainer}>
                    <GridItem {...styles.productDescriptionAndPartNumbersGridItem}>
                        <GridContainer {...styles.productDescriptionAndPartNumbersContainer}>
                            <GridItem {...styles.productBrandAndDescriptionGridItem}>
                                {cartLine.brand && (
                                    <ProductBrand brand={cartLine.brand} extendedStyles={styles.productBrand} />
                                )}
                                <ProductDescription product={cartLine} extendedStyles={styles.productDescription} />
                            </GridItem>
                            {!cartLine.isFixedConfiguration && cartLine.sectionOptions!.length > 0 && (
                                <GridItem {...styles.configurationGridItem}>
                                    {cartLine.sectionOptions!.map(option => (
                                        <Typography {...styles.configurationOptionText} key={option.sectionOptionId}>
                                            {`${option.sectionName}:${option.optionName}`}
                                        </Typography>
                                    ))}
                                </GridItem>
                            )}
                            <GridItem {...styles.productPartNumbersGridItem}>
                                <ProductPartNumbers
                                    productNumber={cartLine.erpNumber}
                                    customerProductNumber={cartLine.customerName}
                                    manufacturerItem={cartLine.manufacturerItem}
                                    extendedStyles={styles.productPartNumbers}
                                />
                            </GridItem>
                            {cartLine.isRestricted && (
                                <GridItem {...styles.cartLineErrorMessageGridItem}>
                                    <Typography
                                        {...styles.cartLineErrorMessageText}
                                        data-test-selector="cartline_restrictedMessage"
                                    >
                                        {translate("Restricted product")}
                                    </Typography>
                                </GridItem>
                            )}
                            {!cartLine.isActive && (
                                <GridItem {...styles.cartLineErrorMessageGridItem}>
                                    <Typography {...styles.cartLineErrorMessageText}>
                                        {translate("Inactive Product")}
                                    </Typography>
                                </GridItem>
                            )}
                        </GridContainer>
                    </GridItem>
                    <GridItem {...styles.productPriceAndQuantityGridItem}>
                        <GridContainer {...styles.productPriceAndQuantityContainer}>
                            <GridItem {...styles.priceGridItem}>
                                {showPriceMessage ? (
                                    <StyledWrapper>
                                        <Typography {...styles.notFoundLabel}>{translate("Your Price:")}</Typography>
                                    </StyledWrapper>
                                ) : (
                                    <StyledWrapper>
                                        <Typography {...styles.notFoundLabel}>{translate("List Price:")}</Typography>
                                    </StyledWrapper>
                                )}
                                <ProductPrice
                                    product={cartLine}
                                    showSavings={true}
                                    showSavingsAmount={showSavingsAmount}
                                    showSavingsPercent={showSavingsPercent}
                                    showInvalidPriceMessage={showInvalidPriceMessage}
                                    extendedStyles={styles.price}
                                    showLabel={false}
                                />
                                {!showPriceMessage ? (
                                    <StyledWrapper {...styles.itemNotFound}>
                                        <Link href="/contact-kgpco">{translate("Contact Us")}</Link>
                                        <Typography>
                                            {translate("for pricing & availability for this item.")}
                                        </Typography>
                                    </StyledWrapper>
                                ) : null}
                                {promotions?.map(promotion => (
                                    <Typography
                                        {...styles.promotionNameText}
                                        key={promotion.id}
                                        data-test-selector="orderLineCardExpandedPromotionName"
                                    >
                                        {promotion.name}
                                    </Typography>
                                ))}
                            </GridItem>
                            {showPriceMessage && (
                                <GridItem {...styles.quantityGridItem} data-test-selector="orderConformation_Quantity">
                                    <CartLineQuantity
                                        cart={cart}
                                        editable={editable}
                                        extendedStyles={styles.quantity}
                                    />
                                </GridItem>
                            )}
                            <GridItem {...styles.extendedUnitNetPriceGridItem}>
                                {cartLine.pricing && (showPricing || isCheckoutReviewAndSubmitPage) && (
                                    <>
                                        {enableVat && vatPriceDisplay === "DisplayWithAndWithoutVat" && (
                                            <>
                                                <SmallHeadingAndText
                                                    heading={`${translate("SUBTOTAL")} (${translate("Ex. VAT")})`}
                                                    text={cartLine.pricing.extendedUnitNetPriceDisplay}
                                                    extendedStyles={styles.subtotalWithoutVatText}
                                                />
                                                <SmallHeadingAndText
                                                    heading={`${translate("Total VAT")} (${cartLine.pricing.vatRate}%)`}
                                                    text={cartLine.pricing.vatAmountDisplay}
                                                    extendedStyles={styles.vatAmount}
                                                />
                                            </>
                                        )}
                                        <SmallHeadingAndText
                                            heading={
                                                !enableVat
                                                    ? translate("SUBTOTAL")
                                                    : vatPriceDisplay !== "DisplayWithoutVat"
                                                    ? `${translate("Subtotal")} (${translate("Inc. VAT")})`
                                                    : `${translate("Subtotal")} (${translate("Ex. VAT")})`
                                            }
                                            text={
                                                enableVat && vatPriceDisplay !== "DisplayWithoutVat"
                                                    ? cartLine.pricing.extendedUnitRegularPriceWithVatDisplay
                                                    : cartLine.pricing.extendedUnitNetPriceDisplay
                                            }
                                            extendedStyles={styles.extendedUnitNetPrice}
                                        />
                                    </>
                                )}
                            </GridItem>
                        </GridContainer>
                    </GridItem>
                    <GridItem {...styles.dateInfoGridItem}>
                        {/* custom propperty start */}
                        <StyledWrapper {...styles.fromDate}>
                            <Typography as="p" {...styles.earlistShipDateLabel}>
                                {translate("Requested Delivery Date")}
                            </Typography>
                            <Typography as="p" {...styles.earlistShipDate}>
                                {cartLine.properties.requiredDate || "Not Available"}
                            </Typography>
                        </StyledWrapper>
                        {/* <StyledWrapper {...styles.esdWrapper}>
                            <Typography as="p" {...styles.earlistShipDateLabel}>
                                {translate("Earliest Ship Date")}
                            </Typography>
                            <Typography as="p" {...styles.earlistShipDate}>
                                {cartLine.properties.earliestShipDate || "Not Available"}
                            </Typography>
                        </StyledWrapper> */}
                        {isAuthenticated && showLeadTime && leadtime && leadtime !== "" && (
                            <GridItem {...styles.seeLeadTimeLinkGridItem}>
                                <Link {...styles.seeLeadTimeLink} data-test-selector="seeLeadTimeLink">
                                    {translate("See Lead Time")}
                                    <StyledWrapper
                                        className="tooltipText"
                                        {...styles.tooltipText}
                                    >{`${leadtime} days`}</StyledWrapper>
                                </Link>
                            </GridItem>
                        )}
                        {/* custom propperty end */}
                    </GridItem>
                    <GridItem {...styles.productMessageGridItem}>
                        {/* custom propperty start */}
                        {cartLine.properties.productMessage && (
                            <Typography as="p" {...styles.cartMsg}>
                                {cartLine.properties.productMessage}
                            </Typography>
                        )}
                        {cartLine.properties.isProductNonReturnable && (
                            <Typography as="p" {...styles.cartMsg}>
                                {siteMessage("This item cannot be returned.")}
                            </Typography>
                        )}
                        {/* custom propperty end */}
                    </GridItem>
                    {cartLine.notes && (
                        <GridItem {...styles.orderLineNotesGridItem}>
                            <CartLineNotes
                                cart={cart}
                                editable={editable}
                                label={translate("Line Notes")}
                                extendedStyles={styles.cartLineNotes}
                            />
                        </GridItem>
                    )}
                    {cart.showCostCode && cartLine.costCode && (
                        <GridItem {...styles.costCodeGridItem}>
                            <Typography {...styles.costCodeValueText} as="p">
                                <Typography {...styles.costCodeLabelText}>{cart.costCodeLabel}:</Typography>
                                {cartLine.costCode}
                            </Typography>
                        </GridItem>
                    )}
                </GridContainer>
            </GridItem>
        </GridContainer>
    );
};

export default connect(mapStateToProps)(withCartLine(CartLineCardExpanded));
